import {useMemo} from 'react'
import PickupTime from '@/customComponents/sharedComponents/PickupTime.jsx'
import {carIcon, whiteCar, locationIcon, pencilIcon, callIcon, messageIcon, rightArrow, plusIconLight} from "@img"
import {useSidebar} from '@/context/SidebarContext.jsx'
import {useUpcomingTrips} from '@/hooks/useTrips'
import {Link, useNavigate} from 'react-router'
import TripRouteMap from '@/components/map/TripRouteMap.jsx'
import AlertMessage from '@/customComponents/homeComponents/AlertMessage.jsx'
import {
    locationLabel,
    formatTime,
    formatTravelTime,
    getStatusStyle,
    getStatusLabel,
} from '@/utils/functions'
import {parseAppZoneDate, addMinutesToAppZone} from '@/utils/dateUtils'

export default function UpcomingRide() {
    const {isMobile} = useSidebar()
    const navigate = useNavigate()
    const {data, isLoading} = useUpcomingTrips({page: 1, perPage: 5})

    const trip = useMemo(
        () => data?.trips?.find(t => t.status !== 'Requested' && t.status !== 'Draft'),
        [data?.trips]
    )

    const {durationMin, estimatedArrival, polyline, legEnds, lastLegMin} = useMemo(() => {
        if (!trip) return {durationMin: 0, estimatedArrival: '--', polyline: null, legEnds: [], lastLegMin: 0}
        const dur = trip.route?.duration_minutes
        const sched = trip.scheduled_at
        const arrival = sched && dur
            ? formatTime(addMinutesToAppZone(sched, dur))
            : '--'
        const legs = trip.route?.legs || []
        const stopCount = trip.stop_overs?.length || 0
        // Real instant — see RideDetails: the leg-end times render in the viewer's zone.
        const baseMs = parseAppZoneDate(sched)?.getTime() ?? null
        const ends = []
        if (baseMs) {
            let cursor = baseMs
            if (legs.length > 0) {
                for (const leg of legs) {
                    const d = leg.duration_minutes ?? leg.duration_in_minutes ?? (leg.duration_seconds ? Math.round(leg.duration_seconds / 60) : 0)
                    cursor += d * 60000
                    ends.push({time: cursor, durationMin: d})
                }
            } else if (stopCount > 0 && dur > 0) {
                const perLeg = Math.round(dur / (stopCount + 1))
                for (let i = 0; i < stopCount + 1; i++) {
                    cursor += perLeg * 60000
                    ends.push({time: cursor, durationMin: perLeg})
                }
            }
        }
        return {
            durationMin: dur,
            estimatedArrival: arrival,
            polyline: trip.route?.polyline,
            legEnds: ends,
            lastLegMin: ends.length > 0 ? ends[ends.length - 1].durationMin : dur,
        }
    }, [trip])

    if (isLoading) {
        return (
            <div className="white-global-section">
                <div className="flex items-center gap-2 mb-4">
                    <img src={carIcon} alt="car"/>
                    <h5 className="text-lg font-semibold text-gray-900 mb-0! max-md:text-base!">Your Next Ride</h5>
                </div>
                <div className="animate-pulse space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
            </div>
        )
    }

    if (!trip) {
        return (
            <div className="white-global-section">
                <div className="flex items-center gap-2 mb-4">
                    <img src={carIcon} alt="car"/>
                    <h5 className="text-lg font-semibold text-gray-900 mb-0! max-md:text-base!">Your Next Ride</h5>
                </div>
                <p className="text-(--text-secondary) mb-0!">No upcoming rides scheduled</p>
            </div>
        )
    }

    const paymentNotAuthorized = trip.payment_hold?.is_authorized === false || trip.payment_hold?.is_captured === false
    // An initial trip that already spawned a real (non-Draft) return booking can't get another.
    const hasReturnBooking = !!trip.return_booking && trip.return_booking.status !== 'Draft'

    if (isMobile) {
        return (
            <Link to={`/rides/${trip.id}`} className="relative white-global-section bg-white block">
                <AlertMessage paymentHold={trip.payment_hold}/>
                {/* Row 1: status (moved up above the title) + a smaller Return Booking button */}
                <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="flex items-center gap-2 flex-wrap">
                        <span className={`${getStatusStyle(trip.status)} px-4 py-1.5 rounded-full text-xs font-semibold`}>{getStatusLabel(trip.status_label, trip.status)}</span>
                    </span>
                    {!trip.parent_trip && (
                        <span className="relative group">
                            <button type="button"
                                    disabled={paymentNotAuthorized || hasReturnBooking}
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/return-booking/${trip.id}`) }}
                                    className="cursor-pointer flex items-center gap-1.5 py-1.5 pr-3 pl-1.5 bg-(--text-primary) text-white text-xs rounded-full transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50">
                                <img alt="" src={plusIconLight} className="w-5"/> Return Booking
                            </button>
                            {(hasReturnBooking || paymentNotAuthorized) && (
                                <span className="hidden group-hover:block absolute top-full right-0 mt-2 bg-[#3c393c] text-white rounded-lg px-3 py-2 text-xs text-center w-max max-w-[220px] z-30 after:content-[''] after:absolute after:bottom-full after:right-5 after:border-[6px] after:border-transparent after:border-b-[#3c393c]">{hasReturnBooking ? `This trip already has a return booking: ${trip.return_booking.id}` : 'First fix your payment issue'}</span>
                            )}
                        </span>
                    )}
                </div>
                {/* Row 2: the title, now on its own line under the status */}
                <div className="flex items-center gap-2 mb-2">
                    <img src={carIcon} alt="car" className="w-5 h-5"/>
                    <span className="font-bold text-base">Your Next Ride</span>
                </div>
                {/* Row 3: origin + arrow */}
                <div className="flex items-center justify-between">
                    <span className="text-sm text-(--text-secondary)">{locationLabel(trip.origin)}</span>
                    <img src={rightArrow} alt="arrow" className="w-5 h-5 shrink-0"/>
                </div>
            </Link>
        )
    }

    return (
        <div className="relative white-global-section">
            <AlertMessage paymentHold={trip.payment_hold}/>
            <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 md:col-span-7 order-2 md:order-1">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <img src={carIcon} alt="car"/>
                            <h5 className="text-lg font-semibold text-gray-900 mb-0! max-md:text-base!">Your Next Ride</h5>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`${getStatusStyle(trip.status)} px-6 py-2.5 rounded-full max-md:px-3 max-md:py-1 max-md:text-xs max-md:font-semibold`}>{getStatusLabel(trip.status_label, trip.status)}</span>
                            {!trip.parent_trip && (
                                <span className="relative group">
                                    <button type="button"
                                            disabled={paymentNotAuthorized || hasReturnBooking}
                                            onClick={() => navigate(`/return-booking/${trip.id}`)}
                                            className="cursor-pointer flex items-center gap-2 py-2.5 pr-4 pl-2 bg-(--text-primary) text-white rounded-full transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50">
                                        <img alt="" src={plusIconLight} className="w-7"/>
                                        Return Booking
                                    </button>
                                    {(hasReturnBooking || paymentNotAuthorized) && (
                                        <span className="hidden group-hover:block absolute top-full right-0 mt-2 bg-[#3c393c] text-white rounded-lg px-3 py-2 text-xs text-center w-max max-w-[220px] z-30 after:content-[''] after:absolute after:bottom-full after:right-5 after:border-[6px] after:border-transparent after:border-b-[#3c393c]">{hasReturnBooking ? `This trip already has a return booking: ${trip.return_booking.id}` : 'First fix your payment issue'}</span>
                                    )}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="mb-4 text-(--text-secondary) max-md:mb-3 max-md:text-xs">
                        <p className="mb-0!">{locationLabel(trip.origin)}</p>
                    </div>
                    <div className="relative max-md:text-sm">
                        <div className="relative overflow-hidden">
                            <span className="absolute h-full w-px left-3 top-5 border-l-2 border-dashed border-gray-900"></span>

                            {/* Origin */}
                            <div className="flex relative mb-6 min-h-7 before:absolute before:content-[''] before:w-2 before:h-2 before:bg-gray-900 before:rounded-full before:outline-2 before:outline-gray-900 before:outline-offset-5 before:top-2 before:left-2">
                                <div className="w-1/2 pl-10">
                                    <PickupTime trip={trip} format={formatTime}/>
                                </div>
                                <div className="w-1/2">
                                    <span>{locationLabel(trip.origin)}</span>
                                </div>
                            </div>

                            {/* Mid-point (car icon) */}
                            {trip.stop_overs?.length > 0 && trip.stop_overs.map((stop, i) => {
                                const leg = legEnds[i]
                                return (
                                    <div key={i} className="flex relative mb-6 min-h-7">
                                        <img src={whiteCar} alt="car" className="absolute w-6 h-8 top-0.5 left-0 z-10 object-contain"/>
                                        <div className="w-1/2 pl-10">
                                            {leg && <span className="block">{formatTime(new Date(leg.time))}</span>}
                                            {leg?.durationMin > 0 && <span className="block text-gray-400 text-xs">{formatTravelTime(leg.durationMin)}</span>}
                                        </div>
                                        <div className="w-1/2">
                                            <span className="block">{locationLabel(stop.location || stop)}</span>
                                        </div>
                                    </div>
                                )
                            })}

                            {/* Destination */}
                            <div className="flex relative mb-0 min-h-7 before:absolute before:content-[''] before:bg-white before:w-5 before:h-full before:z-10">
                                <img src={locationIcon} alt="destination" className="absolute w-6 h-6 top-0 left-0 z-20"/>
                                <div className="w-1/2 pl-10">
                                    <span className="block">{estimatedArrival}</span>
                                    {lastLegMin > 0 && <span className="block text-gray-400 text-xs">{formatTravelTime(lastLegMin)}</span>}
                                </div>
                                <div className="w-1/2">
                                    <span className="block">{locationLabel(trip.destination)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col items-center md:flex-row md:items-start gap-3 mt-5">
                        <Link to={`/rides/${trip.id}`} className="bg-(--bg-dark) rounded-full flex items-center gap-3 px-5 py-4 cursor-pointer">
                            <span className="text-white">Trip info</span>
                            <img src={pencilIcon} alt="edit"/>
                        </Link>

                        {trip.driver?.driver_id && (
                            <div className="bg-(--bg-grey) rounded-full flex items-center gap-2 px-1 py-1 pl-5">
                                Contact Driver
                                <button className="cursor-pointer flex items-center justify-center bg-(--bg-dark) w-12 h-12 rounded-full"><img src={messageIcon} alt="message"/></button>
                                <button className="cursor-pointer flex items-center justify-center bg-(--bg-dark) w-12 h-12 rounded-full"><img src={callIcon} alt="call"/></button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="col-span-12 md:col-span-5 order-1 md:order-2">
                    <TripRouteMap
                        polyline={polyline}
                        origin={typeof trip.origin === 'object' ? trip.origin : null}
                        destination={typeof trip.destination === 'object' ? trip.destination : null}
                        stopOvers={trip.stop_overs}
                        legs={trip.route?.legs}
                        summary={trip.route?.summary}
                        mapUrl={trip.route?.map_url}
                        className="h-full"
                    />
                </div>
            </div>
        </div>
    )
}
